import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ErrorAlert, LoadingSpinner } from "../../components/ui";
import { MolduraDeAcesso } from "../auth/components/MolduraDeAcesso";
import { mensagemDeErro } from "../chamados/gestao/api";
import { useAuthStore } from "../../stores/authStore";
import { entrarComTokenMicrosoft, escolherContrato } from "./api";
import { entradaEquipe } from "./config";
import {
  concluirVoltaDaMicrosoft,
  ehVoltaDaMicrosoft,
  entrarComMicrosoft,
} from "./microsoft";
import { lembrarContrato, marcarEntrada, ultimoContrato } from "./sessao";

/** Logotipo da Microsoft, como pede o guia de marca para botões de entrada. */
const LogoMicrosoft = () => (
  <svg viewBox="0 0 21 21" className="h-5 w-5" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const Selo = () => (
  <span className="inline-block rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-800">
    Equipe T4i
  </span>
);

/**
 * Entrada da equipe T4i (/equipe). Endereço próprio, fora do login do
 * cliente: lá fica só um link discreto no rodapé.
 *
 * A mesma página recebe a volta da Microsoft (levarVoltaParaEquipe traz o
 * fragmento para cá): troca o id_token pela sessão do portal e entra direto no
 * último cliente atendido, ou pede para escolher um.
 */
const EntradaEquipe = () => {
  const navegar = useNavigate();
  const clienteQuery = useQueryClient();
  const usuario = useAuthStore((s) => s.user);
  const autenticado = useAuthStore((s) => s.isAuthenticated);

  const [voltando] = useState(() => ehVoltaDaMicrosoft());
  const [ocupado, setOcupado] = useState(voltando);
  const [erro, setErro] = useState<string | null>(null);

  // O StrictMode monta duas vezes em desenvolvimento; a troca do token tem que
  // acontecer uma vez só.
  const iniciou = useRef(false);

  useEffect(() => {
    if (!voltando || iniciou.current) return;
    iniciou.current = true;

    (async () => {
      let idToken: string | null;
      try {
        idToken = await concluirVoltaDaMicrosoft();
      } catch (e) {
        console.error("Volta do login Microsoft", e);
        setErro("A Microsoft não concluiu a entrada. Tente de novo.");
        setOcupado(false);
        return;
      }
      if (!idToken) {
        setOcupado(false);
        return;
      }

      try {
        const sessao = await entrarComTokenMicrosoft(idToken);
        marcarEntrada("equipe");
        clienteQuery.clear();
        useAuthStore.getState().setUser(sessao);

        // Volta para o último contrato atendido, se a pessoa ainda está na equipe dele.
        const ultimo = ultimoContrato(sessao.email);
        if (ultimo) {
          try {
            useAuthStore.getState().setUser(await escolherContrato(ultimo));
            navegar("/", { replace: true });
            return;
          } catch {
            lembrarContrato(sessao.email, null);
          }
        }
        navegar("/equipe/contratos", { replace: true });
      } catch (e) {
        setErro(mensagemDeErro(e, "Não foi possível entrar. Tente de novo."));
        setOcupado(false);
      }
    })();
  }, [voltando, navegar, clienteQuery]);

  // Quem já está como equipe não precisa entrar de novo.
  if (!voltando && autenticado && usuario?.equipe) {
    return <Navigate to={usuario.idContrato ? "/" : "/equipe/contratos"} replace />;
  }

  const entrar = async () => {
    setErro(null);
    setOcupado(true);
    try {
      await entrarComMicrosoft();
    } catch (e) {
      console.error("Saída para o login Microsoft", e);
      setErro("Não foi possível abrir o login da Microsoft. Tente de novo.");
      setOcupado(false);
    }
  };

  return (
    <MolduraDeAcesso
      selo={<Selo />}
      titulo="Atendimento de chamados"
      subtitulo="Entre com a sua conta Microsoft da T4i."
    >
      {!entradaEquipe.configurada ? (
        <p className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-600">
          A entrada da equipe não está configurada neste ambiente.
        </p>
      ) : ocupado && voltando ? (
        <div className="flex flex-col items-center gap-3 py-4 text-sm text-gray-600">
          <LoadingSpinner />
          Entrando…
        </div>
      ) : (
        <div className="space-y-4">
          <button
            type="button"
            onClick={entrar}
            disabled={ocupado}
            className="flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-800 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-wait disabled:opacity-70"
          >
            <LogoMicrosoft />
            {ocupado ? "Abrindo a Microsoft…" : "Entrar com a conta Microsoft"}
          </button>

          {erro && (
            <ErrorAlert
              title="Não foi possível entrar"
              message={erro}
              onClose={() => setErro(null)}
              variant="error"
            />
          )}

          <p className="text-center text-xs text-gray-500">
            Precisa do perfil de atendimento de chamados no ProService.
          </p>
        </div>
      )}

      <div className="pt-6 text-center">
        <Link
          to="/login"
          className="text-sm font-medium text-blue-600 transition-colors hover:text-blue-500"
        >
          Sou cliente: voltar ao login
        </Link>
      </div>
    </MolduraDeAcesso>
  );
};

export default EntradaEquipe;
